import React, { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useNavigate, useParams } from 'react-router-dom';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import { Word, WordType } from '@goldcast/player-config/src/types/transcript-types';
import {
  getClipBroadcastId,
  getAllSegments,
  getClipFPS,
  getEndForSegment,
  getIsDuplicateRecording,
  getStartForSegment,
  isClipFromRecordingStudio,
  isClipInOriginalLanguage,
  isFullLengthClip,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { LanguageOption } from '@goldcast/player-config/src/types/clip-types';
import { getCaptionsForSegment } from '@goldcast/player-config/src/utils/playerConfigUtils';
import { ClipDeletes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { getDeletesForSegmentPure } from '@goldcast/player-config/src/utils/timeUtils';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import {
  selectSpeakerDetails,
  selectTranscript,
  selectTranscriptLoading
} from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import {
  getParagraphIndexByTime,
  parseTranscriptToParagraphs,
  timeSpeakerDetailsStringToSeconds,
  getActiveWords,
  getActivePauseEntry,
  isWordDeleted
} from './utils';
import { hasDeleteAtPause } from './pauseUtils';
import TranscriptWord from './TranscriptWord';
import { HighlightedSearchParagraph, TranscriptParagraph, TranscriptPauseEntry } from './types';
import { CLIP_MIN_LENGTH, TRANSCRIPT_VIRTUOSO_COMPONENT_ID } from './constants';
import TranscriptSearch from './TranscriptSearch';
import TranscriptDownloadButton from './TranscriptDownloadButton';
import EditTranscriptMenu from './EditTranscriptMenu';
import CreateClipPopup from './CreateClipPopup';
import useTranscriptSelect from './useTranscriptSelect';
import useFullClipTranscriptHighlight from './useFullClipTranscriptHighlight';
import TranscriptInlineEdit from './TranscriptInlineEdit/TranscriptInlineEdit';
import TranscriptPauseBubble from './TranscriptPauseBubble';
import TranscriptDeadSpaceRow from './TranscriptDeadSpaceRow';
import { useTranscriptAutoScroll } from './useTranscriptAutoScroll';
import JumpToCurrentTime from './JumpToCurrentTime';
import useVirtuosoScroll from './useVirtusoScroll';
import useDeletesWithDragChanges from './useDeletesWithDragChanges';
import usePauseOperations from './usePauseOperations';
import useClipPauseEntries from './useClipPauseEntries';
import { TranslationLanguageSelectDropdown } from './ClipTranscript/TranslationLanguageSelectDropdown';
import { RegenerateTranslationNudge } from './ClipTranscript/RegenerateTranslationNudge';
import TranscriptSkeleton from './TranscriptSkeleton';
import useTranscriptExtension from './useTranscriptExtension';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { classnames, preventDefault } from '@/libs/utils';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import useElementScrolledHook from '@/hooks/useElementScrolledHook';
import { currentClip } from '@/stores/clip';
import { playerV2 } from '@/stores/playerV2';
import Loader from '@/components/atoms/Loader';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import ChapterActionsMenu from '@/Pages/Clip/ClipPlayer/ClipTimeline/ChapterActionsMenu';
import { formatTime } from '@/libs/core';
import { ChapterType } from '@/Pages/Clip/ClipPlayer/ClipTimeline/ClipTimelineTypes';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';
import FillerWordsMenu from '@/components/molecules/FillerWordsMenu/FillerWordsMenu';
import featureFlagStore from '@/stores/featureFlagStore';
import CopyClipButton from '@/Pages/Clip/ClipPlayer/ClipCustomizer/pocAIEdits/components/CopyClipButton';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { TIMELINE_COMPONENT_CLASS_IDENTIFIER } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/constants';
import useTranslationState from '@/hooks/useTranslationState';
import CaptionLanguageSelectionDialog from '@/Pages/Clip/CaptionTranslation/CaptionLanguageSelectionDialog';
import useDialog from '@/components/organisms/useDialog';
import { ADD_MENU_POPOVER_TEST_ID } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelinePlayerActionBar/AddMenu/constants';

import { remotionPlayerStateStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import {
  getPlayerTimeFromSegmentTime,
  getSegmentAtCurrentFrame,
  getSegmentTimeFromPlayerTime
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import ClapperIcon from '@/components/atoms/ClapperIcon';
import {
  getSegmentDisplayTextWithIntroOutroPrefix,
  truncateDisplayText
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/utils';
import { useRemotionPlayerPause } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerPause';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import Icon from '@/components/atoms/Icon';

function FullTranscript({
  videoAssetId,
  title = 'Transcript',
  onCreateClip,
  onPause = () => null,
  fullClipOffset = 0,
  disableRedirectAfterCreateClip = false,
  getRenderTime,
  hideEndTimeStamp = false
}: {
  videoAssetId?: string;
  title?: string;
  onCreateClip: (
    startTime: number | null,
    endTime: number | null,
    startSegmentId?: string,
    endSegmentId?: string
  ) => Promise<void>;
  onPause?: () => void;
  fullClipOffset?: number;
  disableRedirectAfterCreateClip?: boolean;
  getRenderTime?: (segmentTime: number, segmentId: string) => string;
  hideEndTimeStamp?: boolean;
}) {
  const navigate = useNavigate();
  const { eventId, broadcastId } = useParams<{ eventId: string; broadcastId: string }>();

  const virtuoso = useRef<VirtuosoHandle>(null);
  const container = useRef<HTMLDivElement>(null);
  const virtuosoWrapperRef = useRef<HTMLDivElement>(null);
  const createClipWrapper = useRef<HTMLDivElement>(null);
  const createClipPopup = useRef<HTMLDivElement>(null);
  const chapterButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [highlightedParagraph, setHighlightedParagraph] = useState<HighlightedSearchParagraph | null>(null);
  const transcriptStore = useTranscriptContext();
  const [isCreatingClip, setIsCreatingClip] = useState<boolean | undefined>(undefined);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const { seekTo } = useRemotionPlayerSeek();

  const { isPlaying: isPlayerPlaying } = useRemotionPlayerPause();
  const [activeHighlightedWord, setActiveHighlightedWord] = useState<{
    activeWords: Word[];
    segmentId: string;
  } | null>(null);
  const [activeHighlightedPause, setActiveHighlightedPause] = useState<{
    segmentId: string;
    startTime: number;
  } | null>(null);

  const { clipCaptionsLoading, clipId, clipData } = useClipsContext();
  const { transcriptExtension, setTranscriptExtension } = useTranscriptExtension({ clipData });
  const clipBroadcastId = useMemo(() => getClipBroadcastId(clipData) ?? '', [clipData]);
  const sourceTranscript = useContentMetadataStore(selectTranscript(clipBroadcastId));
  const speakersWithDetails = useContentMetadataStore(selectSpeakerDetails(clipBroadcastId));
  const isTranscriptLoaded = useContentMetadataStore(selectTranscriptLoading(clipBroadcastId));

  const hasInitializedTranscript = useMemo(() => {
    return !isTranscriptLoaded;
  }, [isTranscriptLoaded]);

  const allSegments = useMemo(() => getAllSegments(clipData), [clipData]);

  const isTranslated = useMemo(() => {
    return !isClipInOriginalLanguage(clipData);
  }, [clipData]);

  const transcript = useMemo(() => {
    if (sourceTranscript.length > 0 && isTranslated) {
      return allSegments.reduce((acc, segment) => {
        if (segment.type !== SegmentType.COMPOSITE) {
          return {
            ...acc,
            [segment.id]: []
          };
        }
        return {
          ...acc,
          [segment.id]:
            getCaptionsForSegment({
              clip: clipData,
              segmentId: segment.id,
              isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
            }) ?? []
        };
      }, {}) as Record<string, Word[]>;
    }
    return allSegments.reduce((acc, segment) => {
      if (segment.type !== SegmentType.COMPOSITE) {
        return {
          ...acc,
          [segment.id]: []
        };
      }
      return { ...acc, [segment.id]: sourceTranscript };
    }, {}) as Record<string, Word[]>;
  }, [sourceTranscript, isTranslated, allSegments, clipData]);

  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);
  const { handleOnScroll: handleScrolled } = useElementScrolledHook();

  const editPopupRef = useRef<HTMLDivElement>(null);
  const allClips = useSyncExternalStore(currentClip.subscribe, currentClip.getSnapshot);
  const [showEditMenu, setShowEditMenu] = useState(false);
  const { updateChapter, createNewChapter, selectedChapter, setSelectedChapter } = useTranscriptPage();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isFullClip = useMemo(() => {
    return isFullLengthClip(clipData);
  }, [clipData]);

  const isClipFromRS = useMemo(() => {
    return isClipFromRecordingStudio(clipData);
  }, [clipData]);

  const canUseFillerWords = useMemo(() => {
    return !!featureFlags[FeatureFlagKeys.USE_CL_FILLER_WORDS];
  }, [featureFlags]);

  const canShowCopyButton = useMemo(() => {
    return !!featureFlags[FeatureFlagKeys.Use_CL_Copy_Clip_To_Test_AiEdits];
  }, [featureFlags]);

  const isSplitsEnabled = useMemo(() => {
    return isSplitsInTimelineEnabledPure({
      clip: clipData,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
  }, [clipData]);

  const isSplitsTranslationEnabled = useMemo(() => {
    return featureFlags[FeatureFlagKeys.Use_CL_Splits_Translations];
  }, [featureFlags]);

  const isLanguageDropdownVisible = useMemo(() => {
    return (
      !isFullClip &&
      !getIsDuplicateRecording(clipData) &&
      !isSearchActive &&
      (!isSplitsEnabled || isSplitsTranslationEnabled)
    );
  }, [isFullClip, clipData, isSearchActive, isSplitsEnabled, isSplitsTranslationEnabled]);

  const canShowFillerWordsMenu = useMemo(() => {
    return canUseFillerWords && !!featureFlags[FeatureFlagKeys.Use_CL_Filler_Words_Menu];
  }, [canUseFillerWords, featureFlags]);

  const isPausesEnabled = !!featureFlags[FeatureFlagKeys.Use_Pauses_Enhancements];

  const [isEditing, setIsEditing] = useState(false);
  const [editingStartTime, setEditingStartTime] = useState<number | undefined>();
  const [editingSegmentId, setEditingSegmentId] = useState<string | undefined>();

  const [editingContent, setEditingContent] = useState<string | undefined>();
  const { scrollToVirtuosoIndex } = useVirtuosoScroll({
    virtuosoRef: virtuoso,
    containerRef: container
  });
  const editingTranscriptRef = useRef<HTMLInputElement>(null);

  const isEdit = useMemo(() => {
    return !!videoAssetId;
  }, [videoAssetId]);

  // What is the reason for this and not use clipData directly?
  const videoAssetClip = useMemo(() => {
    if (isSplitsEnabled) {
      return clipData;
    }
    return videoAssetId ? allClips[videoAssetId] : undefined;
  }, [allClips, clipData, videoAssetId, isSplitsEnabled]);

  const isChaptersEnabled = useMemo(() => {
    return isFullLengthClip(clipData) && !getIsDuplicateRecording(clipData);
  }, [clipData]);

  const {
    isTranslationProcessing,
    translatedLanguagesOptions,
    onCaptionTranslationConfirm,
    selectedLanguageName,
    onLanguageSelection,
    allowCaptionTranslation,
    handleCaptionTranslationRestricted,
    hasMultipleTranslatedLanguages,
    isOriginalLanguageSupportedForTranslation,
    checkLanguageAndExecute,
    isWarningModalOpen,
    closeWarningModal
  } = useTranslationState(clipData);

  const {
    isOpen: isCaptionTranslationModalOpen,
    openDialog: openCaptionTranslationModal,
    closeDialog: closeCaptionTranslationModal
  } = useDialog();

  const onCaptionTranslation = useCallback(
    (selectedLanguage: LanguageOption) => {
      onCaptionTranslationConfirm(selectedLanguage);
      closeCaptionTranslationModal();
    },
    [onCaptionTranslationConfirm, closeCaptionTranslationModal]
  );

  const videoAssetClipDeletes = useMemo(() => {
    const clipDeletes = videoAssetClip
      ? allSegments.reduce(
          (acc, segment) => {
            if (segment.type !== SegmentType.COMPOSITE) {
              return acc;
            }
            return {
              ...acc,
              [segment.id]: getDeletesForSegmentPure({
                clip: videoAssetClip,
                segmentId: segment.id,
                isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
              })
            };
          },
          {} as Record<string, ClipDeletes>
        )
      : ({} as Record<string, ClipDeletes>);
    return clipDeletes;
  }, [allSegments, videoAssetClip]);

  const { newDeletes: deletesWithDragChanges } = useDeletesWithDragChanges({ allDeletes: videoAssetClipDeletes });

  // using startTime and endTime needs to be removed else will cause a lot of rerenders
  const {
    startTime: selectionStartTime,
    endTime: selectionEndTime,
    nearestSelectionStartIndex,
    nearestSelectionEndIndex,
    nearestStartSelectedSegmentId,
    nearestEndSelectedSegmentId,
    nearestStartSelectedSegmentIndex,
    nearestEndSelectedSegmentIndex,
    startSegmentId,
    endSegmentId,
    onCleanupSelection,
    transcriptSelection,
    isSelecting
  } = useTranscriptSelect({
    transcript,
    allDeletes: deletesWithDragChanges
  });

  useEffect(() => {
    const listener = () => {
      if (isPlayerPlaying) {
        setActiveHighlightedWord(null);
        return;
      }
      const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
      const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
      if (!currentSegment) {
        setActiveHighlightedWord(null);
        return;
      }
      const { activeWords } = getActiveWords({ currentTime, transcript, clipData });
      if (activeWords?.length > 0) {
        setActiveHighlightedWord({ activeWords, segmentId: currentSegment.id });
        return;
      }
      setActiveHighlightedWord(null);
    };

    const unsubscribe = remotionPlayerStateStore.subscribe(listener);
    listener();
    return () => {
      setActiveHighlightedWord(null);
      unsubscribe();
    };
  }, [clipData, isPlayerPlaying, transcript]);

  const { paragraphs, firstIndex }: { paragraphs: TranscriptParagraph[]; firstIndex: number } = useMemo(() => {
    let paragraphs = parseTranscriptToParagraphs(
      transcript,
      speakersWithDetails,
      videoAssetClip,
      chapters,
      isTranslated,
      transcriptExtension
    );
    if (!isChaptersEnabled) {
      paragraphs = paragraphs.filter(p => !p.chapterDetails);
    }
    // In case of the edit full recording, scroll to first non-deleted word
    const firstCompositeSegmentId = Object.keys(paragraphs)[0];
    if (!firstCompositeSegmentId) {
      return { paragraphs: [] as TranscriptParagraph[], firstIndex: -1 };
    }
    const firstIndex = paragraphs.findIndex(p => p.words.some(w => !w.is_deleted));
    return { paragraphs, firstIndex: firstIndex };
  }, [transcript, speakersWithDetails, videoAssetClip, chapters, isTranslated, transcriptExtension, isChaptersEnabled]);

  const transcriptWithDeletedWordStatus = useMemo(() => {
    return Object.entries(transcript).reduce(
      (acc, [compositeSegmentId, words]) => {
        return {
          ...acc,
          [compositeSegmentId]: words.map(w => ({
            ...w,
            is_deleted: isWordDeleted(w, videoAssetClipDeletes[compositeSegmentId])
          }))
        };
      },
      {} as Record<string, Word[]>
    );
  }, [transcript, videoAssetClipDeletes]);

  const { highlightFullClipActiveWord } = useFullClipTranscriptHighlight({
    transcript: transcriptWithDeletedWordStatus,
    container,
    activeHighlightedPause
  });

  // Track which pause entry the playback cursor sits in (when paused and no active word)
  useEffect(() => {
    if (!isPausesEnabled) {
      return;
    }
    const listener = (): void => {
      const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
      const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
      if (!currentSegment || currentSegment.type !== SegmentType.COMPOSITE) {
        setActiveHighlightedPause(null);
        return;
      }
      const compositeSegmentTime = getSegmentTimeFromPlayerTime(currentTime, clipData, currentSegment.id);
      const activePause = getActivePauseEntry(compositeSegmentTime, currentSegment.id, paragraphs);
      if (activePause) {
        setActiveHighlightedPause({ segmentId: currentSegment.id, startTime: activePause.startTime });
      } else {
        setActiveHighlightedPause(null);
      }
    };

    const unsubscribe = remotionPlayerStateStore.subscribe(listener);
    listener();
    return () => {
      setActiveHighlightedPause(null);
      unsubscribe();
    };
  }, [clipData, paragraphs, isPausesEnabled]);

  const { continueAutoScroll, isAutoScrollDisabled, onPreventAutoScroll } = useTranscriptAutoScroll({
    transcriptWithDeletedWordStatus,
    paragraphs,
    scrollToVirtuosoIndex
  });

  const handleOnScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      handleScrolled(e);
    },
    [handleScrolled]
  );

  const checkAndScrollToVirtuosoIndex = useCallback(
    ({
      paragraphIndex,
      segmentId,
      wordId,
      respectAutoScrollDisabled = true
    }: {
      paragraphIndex: number;
      segmentId: string;
      wordId?: number;
      respectAutoScrollDisabled?: boolean;
    }) => {
      if (isAutoScrollDisabled && respectAutoScrollDisabled) {
        return;
      }

      scrollToVirtuosoIndex({ paragraphIndex, segmentId, wordId });
    },
    [isAutoScrollDisabled, scrollToVirtuosoIndex]
  );

  useEffect(() => {
    let previousTimeStamp = remotionPlayerStateStore.getSnapshot().currentTime;
    let previousIsActiveSegmentId = getSegmentAtCurrentFrame(previousTimeStamp, clipData)?.id || '';
    let lastActiveWords: Word[] = [];
    const highlightActiveWord = () => {
      requestAnimationFrame(() => {
        highlightFullClipActiveWord();
      });
    };
    const listener = () => {
      const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
      const currentIsActiveSegmentId = getSegmentAtCurrentFrame(currentTime, clipData)?.id || '';

      if (previousTimeStamp !== currentTime || previousIsActiveSegmentId !== currentIsActiveSegmentId) {
        const { activeWords, currentSegment } = getActiveWords({ currentTime, transcript, clipData });
        if (!currentSegment) {
          return;
        }
        const currentSegmentId = currentSegment.id;
        if (activeWords.length === 0) {
          // This is used to remove already highlighted words
          const currentSegmentStartTime = currentSegment.fromFrame / getClipFPS(clipData);
          const paragraphIndex = getParagraphIndexByTime(currentSegmentStartTime, paragraphs || [], currentSegment.id);
          checkAndScrollToVirtuosoIndex({ paragraphIndex, segmentId: currentSegmentId });
          highlightActiveWord();
          return;
        }
        const paragraphIndex = getParagraphIndexByTime(activeWords[0].start_time, paragraphs || [], currentSegment.id);
        if (lastActiveWords[0]?.index === activeWords[0].index) {
          highlightActiveWord();
          return;
        }
        lastActiveWords = activeWords;
        checkAndScrollToVirtuosoIndex({ paragraphIndex, segmentId: currentSegmentId, wordId: activeWords[0].index });
        highlightActiveWord();
      }
      previousTimeStamp = currentTime;
      previousIsActiveSegmentId = currentIsActiveSegmentId || '';
    };
    const unsubscribe = playerV2.subscribe(listener);
    return () => {
      unsubscribe();
    };
  }, [
    highlightFullClipActiveWord,
    clipId,
    transcript,
    checkAndScrollToVirtuosoIndex,
    fullClipOffset,
    paragraphs,
    clipData
  ]);

  const onTimelineForcedScroll = useCallback(
    ({ time, segmentId }: { time: number; segmentId: string }) => {
      const allSegments = getAllSegments(clipData);
      let segment = allSegments.find(s => s.id === segmentId);
      let segmentTime = time;
      if (!segment) {
        return;
      }
      if (segment.type !== SegmentType.COMPOSITE) {
        let diffIndex = 0;
        const currentSegmentIndex = allSegments.findIndex(s => s.id === segmentId);
        while (currentSegmentIndex - diffIndex > 0 || currentSegmentIndex + diffIndex < allSegments.length) {
          const previousSegment = allSegments[currentSegmentIndex - diffIndex];
          const nextSegment = allSegments[currentSegmentIndex + diffIndex];
          if (previousSegment && previousSegment.type === SegmentType.COMPOSITE) {
            segment = previousSegment;
            segmentTime = getEndForSegment({
              clip: clipData,
              segmentId: previousSegment.id,
              isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
            });
            break;
          }
          if (nextSegment && nextSegment.type === SegmentType.COMPOSITE) {
            segment = nextSegment;
            segmentTime = getStartForSegment({
              clip: clipData,
              segmentId: nextSegment.id,
              isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
            });
            break;
          }
          diffIndex++;
        }
      }
      if (!segment) {
        return;
      }
      const closestStart = transcript[segment.id].reduce((prev, { start_time: curr, type }) => {
        return Math.abs(curr - segmentTime) < Math.abs(prev - segmentTime) && type !== WordType.Punctuation
          ? curr
          : prev;
      }, -Infinity);
      const paragraphIndex = getParagraphIndexByTime(closestStart, paragraphs, segment.id);
      if (paragraphIndex < 0 || !paragraphs[paragraphIndex].words.length) {
        return;
      }
      scrollToVirtuosoIndex({
        paragraphIndex,
        segmentId: segment.id,
        wordId: paragraphs[paragraphIndex].words[0].index
      });
    },
    [clipData, transcript, paragraphs, scrollToVirtuosoIndex]
  );

  const clipLanguage = useMemo(() => {
    return clipData.asset_metadata.selected_lang;
  }, [clipData]);

  const isFirstIndexValid = useMemo(() => {
    return firstIndex >= 0;
  }, [firstIndex]);

  useEffect(() => {
    if (isFirstIndexValid) {
      const paragraph = paragraphs[firstIndex];
      const segmentId = paragraph?.segmentId;
      if (segmentId) {
        setTimeout(() => {
          checkAndScrollToVirtuosoIndex({
            paragraphIndex: firstIndex,
            segmentId,
            wordId: paragraph.words?.find(item => !item.is_deleted)?.index,
            respectAutoScrollDisabled: false
          });
        }, 500);
      }
    }
  }, [clipLanguage, isFirstIndexValid, clipId]);

  const cleanUpSelection = useCallback(() => {
    createClipPopup.current?.classList.add('invisible');
    setShowEditMenu(false);
    setIsEditing(false);
    onCleanupSelection();
  }, [onCleanupSelection]);

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      if (
        createClipWrapper.current?.contains(e.target as Node) ||
        editPopupRef.current?.contains(e.target as Node) ||
        editingTranscriptRef.current?.contains(e.target as Node) ||
        document.querySelector(`[data-testid="${ADD_MENU_POPOVER_TEST_ID}"]`)?.contains(e.target as Node) ||
        document
          .getElementsByClassName(TIMELINE_COMPONENT_CLASS_IDENTIFIER.FIXED_WIDTH_TIMELINE)[0]
          ?.contains(e.target as Node)
      ) {
        return;
      }
      cleanUpSelection();
    },
    [createClipWrapper, editPopupRef, cleanUpSelection]
  );

  const showCreateClipPopup = useCallback(
    (selectionWords: Record<string, Word[]>, left: number, top: number) => {
      if (!isEdit && Object.values(selectionWords).flat().length < CLIP_MIN_LENGTH) {
        cleanUpSelection();
        return;
      }

      const element = createClipPopup.current;
      if (element) {
        element.classList.remove('invisible');
        element.style.left = `${left}px`;
        element.style.top = `${top}px`;
      }
      setShowEditMenu(true);
    },
    [cleanUpSelection, isEdit]
  );

  const onClipboardCopy = useCallback(
    (event: ClipboardEvent) => {
      if (
        !nearestSelectionStartIndex ||
        !nearestSelectionEndIndex ||
        !nearestStartSelectedSegmentId ||
        !nearestEndSelectedSegmentId
      ) {
        return;
      }

      event.preventDefault();
      const selectedWords = transcriptSelection?.words || [];
      const textToCopy = Object.values(selectedWords)
        .flat()
        .map(word => word.content)
        .join(' ');
      navigator.clipboard.writeText(textToCopy);
    },
    [
      nearestEndSelectedSegmentId,
      nearestSelectionEndIndex,
      nearestSelectionStartIndex,
      nearestStartSelectedSegmentId,
      transcriptSelection?.words
    ]
  );

  useEffect(() => {
    if (isCreatingClip === undefined || isCreatingClip || disableRedirectAfterCreateClip) {
      return;
    }

    navigate(`/${eventId}/${broadcastId}/clips`);
  }, [isCreatingClip, eventId, broadcastId, disableRedirectAfterCreateClip, navigate]);

  useEffect(() => {
    // Disable scroll when create clip popup is visible
    const virtuosoContainer = document.querySelector(`[data-test-id="virtuoso-scroller"]`);
    if (transcriptSelection) {
      if (!isEdit || isEditing) {
        virtuosoContainer?.addEventListener('scroll', preventDefault, false);
        virtuosoContainer?.addEventListener('mousewheel', preventDefault, false);
        virtuosoContainer?.addEventListener('touchmove', preventDefault, false);
      }

      document.addEventListener('copy', onClipboardCopy);
    }
    return () => {
      if (transcriptSelection) {
        if (!isEdit || isEditing) {
          virtuosoContainer?.removeEventListener('scroll', preventDefault, false);
          virtuosoContainer?.removeEventListener('mousewheel', preventDefault, false);
          virtuosoContainer?.removeEventListener('touchmove', preventDefault, false);
        }
        document.removeEventListener('copy', onClipboardCopy);
      }
    };
  }, [transcriptSelection, isEditing, isEdit, onClipboardCopy]);

  const createFullRecordingClip = useCallback(
    (start: number, end: number, startSegmentId?: string, endSegmentId?: string) => {
      setIsCreatingClip(true);
      onCreateClip(start, end, startSegmentId, endSegmentId).then(() => {
        cleanUpSelection();
        setIsCreatingClip(false);
      });
    },
    [cleanUpSelection, onCreateClip]
  );

  const onClickCreateClipButton = useCallback(() => {
    if (transcriptSelection?.words.length && startSegmentId && endSegmentId) {
      const { start_time } = transcriptSelection.words[startSegmentId][0];
      const { end_time = 0 } = transcriptSelection.words[endSegmentId].at(-1) ?? {};
      createFullRecordingClip(start_time, end_time, startSegmentId, endSegmentId);
    }
  }, [transcriptSelection?.words, createFullRecordingClip, startSegmentId, endSegmentId]);

  useEffect(() => {
    if (
      nearestSelectionStartIndex < 0 ||
      nearestSelectionEndIndex < 0 ||
      nearestStartSelectedSegmentId === null ||
      nearestEndSelectedSegmentId === null ||
      selectionStartTime === null ||
      isSelecting
    ) {
      setShowEditMenu(false);
      return;
    }
    if (
      !isEdit &&
      nearestStartSelectedSegmentId === nearestEndSelectedSegmentId &&
      nearestSelectionStartIndex === nearestSelectionEndIndex
    ) {
      const playerTime = getPlayerTimeFromSegmentTime(selectionStartTime, clipData, nearestStartSelectedSegmentId);
      seekTo(playerTime);
      return;
    }

    // Find all elements with data-word-id and data-segment-id attributes
    const startNode = document.querySelector(
      `[data-word-id="${nearestSelectionStartIndex}"][data-segment-id="${nearestStartSelectedSegmentId}"]`
    );
    const endNode = document.querySelector(
      `[data-word-id="${nearestSelectionEndIndex}"][data-segment-id="${nearestEndSelectedSegmentId}"]`
    );

    const wordsToSelect = transcriptSelection?.words;
    let boundingRect;
    let addendum = 0;
    if (startNode?.parentElement) {
      boundingRect = startNode.parentElement.getBoundingClientRect();
      addendum = 20;
    } else if (endNode?.parentElement) {
      boundingRect = endNode?.parentElement.getBoundingClientRect();
      addendum = -60;
    } else {
      return;
    }

    const x = boundingRect.x;
    const y = boundingRect.y + addendum;
    showCreateClipPopup(wordsToSelect ?? {}, x, y);
  }, [
    isSelecting,
    selectionStartTime,
    nearestSelectionStartIndex,
    nearestSelectionEndIndex,
    nearestStartSelectedSegmentId,
    nearestEndSelectedSegmentId,
    isEdit,
    transcriptSelection?.words,
    showCreateClipPopup,
    allSegments,
    clipData,
    transcript,
    seekTo,
    selectionEndTime
  ]);

  useEffect(() => {
    const timelineForcedScrollEventListener = EventBus.on(CustomEvents.TimelineForcedScroll, onTimelineForcedScroll);
    const clipEndedEventListener = EventBus.on(CustomEvents.ClipEnded, () => {
      const paragraph = paragraphs[firstIndex];
      if (paragraph?.segmentId) {
        checkAndScrollToVirtuosoIndex({ paragraphIndex: firstIndex, segmentId: paragraph.segmentId });
      }
    });

    document.addEventListener('pointerdown', onPointerDown);

    return () => {
      EventBus.off(CustomEvents.ClipEnded, clipEndedEventListener);
      EventBus.off(CustomEvents.TimelineForcedScroll, timelineForcedScrollEventListener);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [transcript, checkAndScrollToVirtuosoIndex, firstIndex, onTimelineForcedScroll, onPointerDown]);

  const handleEditSpeaker = useCallback(speakerId => {
    EventBus.dispatch(CustomEvents.OpenContentSettings, { speakerId });
  }, []);

  const handleRenameChapter = useCallback(chapter => {
    setSelectedChapter(chapter);
  }, []);

  const handleRenameSubmit = useCallback(async () => {
    if (!selectedChapter) {
      return;
    }

    await updateChapter(selectedChapter.id, selectedChapter.title);
    setSelectedChapter(null);
  }, [selectedChapter]);

  const startEditing = useCallback(() => {
    setIsEditing(true);
    setShowEditMenu(false);
  }, [transcriptSelection?.words]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    cleanUpSelection();
  }, [cleanUpSelection]);

  useEffect(() => {
    if (isEditing && startSegmentId) {
      const editingContent = Array.from(document.querySelectorAll('.transcript-selection.word'))
        .map(el => el.textContent)
        .join('')
        .trim();
      setEditingContent(editingContent);
      setEditingStartTime(transcriptSelection?.words?.[startSegmentId]?.[0]?.start_time);
      setEditingSegmentId(startSegmentId);
    } else {
      setEditingContent('');
      setEditingStartTime(undefined);
      setEditingSegmentId(undefined);
    }
  }, [isEditing]);

  const isEmptyTranscript = useMemo(() => {
    return paragraphs.length === 0 && hasInitializedTranscript;
  }, [paragraphs, hasInitializedTranscript]);

  const onCreateNewChapter = useCallback(
    async (words, index, segmentId) => {
      const button = chapterButtonRefs.current[index];
      const endTime = words[words.length - 1].end_time;

      const chapterStartTime = isSplitsEnabled ? getPlayerTimeFromSegmentTime(endTime, clipData, segmentId) : endTime;

      if (button) {
        button.disabled = true;
        button.classList.add('bg-gray-300', 'text-gray-500', 'cursor-not-allowed');
        button.classList.remove('bg-slate-100', 'hover:bg-slate-200');
      }
      try {
        await createNewChapter('New Chapter', chapterStartTime);
      } finally {
        if (button) {
          button.disabled = true;
          button.classList.add('bg-slate-100', 'hover:bg-slate-200');
          button.classList.remove('bg-gray-300', 'text-gray-500', 'cursor-not-allowed');
        }
      }
    },
    [clipData, createNewChapter, isSplitsEnabled]
  );

  const onTranscriptRangedChanged = useCallback(
    ({ startIndex, endIndex }: { startIndex: number; endIndex: number }) => {
      EventBus.dispatch(CustomEvents.FullTranscriptRangeChanged, {
        startIndex,
        endIndex
      });
    },
    []
  );

  const { onPauseOptimize, onPauseRecover, onBulkOptimize, onBulkRecover } = usePauseOperations({
    clipId,
    deletesWithDragChanges
  });

  const {
    unoptimized: unoptimizedPauses,
    optimized: optimizedPauses,
    invalid: invalidPauses
  } = useClipPauseEntries(deletesWithDragChanges);

  const getPauseEntryKey = useCallback((entry: TranscriptPauseEntry): string => {
    return `${entry.segmentId}:${entry.startTime}:${entry.endTime}`;
  }, []);

  const invalidPauseEntryKeys = useMemo(() => {
    return new Set(invalidPauses.map(getPauseEntryKey));
  }, [invalidPauses, getPauseEntryKey]);

  const handleOptimizeAll = useCallback((): void => {
    onBulkOptimize(unoptimizedPauses);
  }, [onBulkOptimize, unoptimizedPauses]);

  const handleRestoreAll = useCallback((): void => {
    onBulkRecover(optimizedPauses);
  }, [onBulkRecover, optimizedPauses]);

  useEffect(() => {
    const scrollLister = () => {
      onPreventAutoScroll();
    };
    const virtuosoComponent = document.getElementById(TRANSCRIPT_VIRTUOSO_COMPONENT_ID);
    if (virtuosoComponent) {
      virtuosoComponent.addEventListener('wheel', scrollLister);
      return () => {
        virtuosoComponent.removeEventListener('wheel', scrollLister);
      };
    }
  }, [onPreventAutoScroll, paragraphs.length]);

  const isRegenerateTranslationNudgeVisible = useMemo(() => {
    return !!hasMultipleTranslatedLanguages;
  }, [hasMultipleTranslatedLanguages]);

  const headerHeight = useMemo(() => {
    if (isRegenerateTranslationNudgeVisible) {
      return 112;
    }
    return 56;
  }, [isRegenerateTranslationNudgeVisible]);

  return (
    <React.Fragment>
      <div className="z-20 flex size-full py-2 transition-all duration-[400ms] ease-in-out">
        <div className="relative w-full overflow-hidden pl-4" ref={container} data-testid="transcript-container">
          {/* Loader for edit full recording only when captions are updating */}
          {clipCaptionsLoading && (
            <div className="absolute z-[70] flex size-full items-center justify-center bg-white/70">
              <Loader />
            </div>
          )}
          <div
            className="sticky top-0 z-20 flex w-full flex-col items-center justify-between p-2"
            style={{ height: headerHeight }}
          >
            <div className="flex h-10 w-full items-center justify-between">
              <span className="select-none truncate text-lg font-title leading-tight">{title}</span>
              {!isEmptyTranscript && hasInitializedTranscript && (
                <div className="flex items-center gap-2">
                  {isLanguageDropdownVisible && (
                    <TranslationLanguageSelectDropdown
                      translatedLanguagesOptions={translatedLanguagesOptions}
                      selectedLanguageName={selectedLanguageName}
                      onLanguageSelection={onLanguageSelection}
                      isTranslationProcessing={isTranslationProcessing}
                      openCaptionTranslationModal={openCaptionTranslationModal}
                      allowCaptionTranslation={allowCaptionTranslation}
                      handleCaptionTranslationRestricted={handleCaptionTranslationRestricted}
                      isOriginalLanguageSupportedForTranslation={isOriginalLanguageSupportedForTranslation}
                      checkLanguageAndExecute={checkLanguageAndExecute}
                      isWarningModalOpen={isWarningModalOpen}
                      closeWarningModal={closeWarningModal}
                    />
                  )}
                  {canShowFillerWordsMenu && (
                    <FillerWordsMenu onEditActionTriggered={onPause} onCloseMenu={cleanUpSelection} />
                  )}
                  {canShowCopyButton && <CopyClipButton />}
                  <TranscriptSearch
                    paragraphs={paragraphs}
                    onOccurrencesChange={scrollToVirtuosoIndex}
                    setHighlightedParagraph={setHighlightedParagraph}
                    onSearchStateChange={setIsSearchActive}
                  />
                  <TranscriptDownloadButton clipId={clipId} />
                </div>
              )}
            </div>
            <RegenerateTranslationNudge className="mb-4 mt-2" isVisible={isRegenerateTranslationNudgeVisible} />
          </div>
          {!hasInitializedTranscript ? (
            <TranscriptSkeleton />
          ) : isEmptyTranscript ? (
            <div
              className="flex h-4/5 w-full flex-col items-center justify-center pt-6"
              data-testid="transcript-empty-warning"
            >
              <div className="flex flex-col items-center justify-center">
                <Icon name="IconMicrophoneOff" className="mb-6 size-8 text-gray-600" />
                <h3 className="mb-4 text-base font-semibold text-gray-700">No transcript available</h3>
                <p className="mx-8 max-w-md text-center text-sm text-gray-500">
                  It seems like there is no spoken audio in this recording, and therefore we couldn't derive a valid
                  transcript.
                </p>
              </div>
            </div>
          ) : (
            <div
              className="relative"
              style={{
                height: `calc(100% - ${headerHeight}px)`
              }}
              ref={virtuosoWrapperRef}
            >
              <JumpToCurrentTime
                clipId={clipId}
                virtuosoWrapperRef={virtuosoWrapperRef}
                transcript={transcriptWithDeletedWordStatus}
                paragraphs={paragraphs}
                fullClipOffset={fullClipOffset}
                continueAutoScroll={continueAutoScroll}
                isSelectingTranscript={isSelecting}
                isDraggingDeletes={videoAssetClipDeletes !== deletesWithDragChanges}
              />
              <Virtuoso
                // 93% height because of search
                style={{
                  height: `${paragraphs.length ? '100%' : '0px'}`,
                  overflowX: 'hidden',
                  marginLeft: '0px',
                  marginRight: '0px',
                  userSelect: 'none'
                }}
                className={classnames(
                  'z-[9] leading-transcript text-slate-700 selection:rounded-sm selection:text-white'
                )}
                data={paragraphs}
                ref={virtuoso}
                {...(isFirstIndexValid ? { initialTopMostItemIndex: firstIndex } : {})}
                id={TRANSCRIPT_VIRTUOSO_COMPONENT_ID}
                onScroll={handleOnScroll}
                rangeChanged={onTranscriptRangedChanged}
                itemContent={(index: number, paragraph: TranscriptParagraph) => {
                  const prevParagraph = paragraphs[index - 1];
                  const isFirstParagraph = index === 0;

                  const nextParagraph = paragraphs[index + 1];
                  const newCompositeSegmentStarted =
                    prevParagraph?.segmentId && paragraph.segmentId !== prevParagraph?.segmentId;

                  if (paragraph.speakerDetails) {
                    if (newCompositeSegmentStarted) {
                      return (
                        <div className="flex h-4 w-full items-center justify-center pb-4 text-gray-400/60">
                          <Icon name="IconScissors" className="mr-2 size-4 shrink-0" />
                          <div className="h-4 min-w-0 flex-1 overflow-hidden pr-2 text-xs">{'---'.repeat(125)}</div>
                        </div>
                      );
                    }
                    // Needed for virtuoso since each item is required to have a height
                    return <div className="h-px w-full" />;
                  }

                  if (paragraph.videoSegmentDetails) {
                    const videoSegment = allSegments.find(segment => segment.id === paragraph.segmentId);
                    if (!videoSegment) {
                      return null;
                    }
                    const videoSegmentStartTime = paragraph.videoSegmentDetails.fromFrame / getClipFPS(clipData);
                    const isLastParagraph = index === paragraphs.length - 1;
                    const videoSegmentLabel = getSegmentDisplayTextWithIntroOutroPrefix(videoSegment);
                    return (
                      <div className={classnames({ 'pb-4': isLastParagraph })}>
                        {!isFirstParagraph && prevParagraph.segmentId !== paragraph.segmentId && (
                          <div className="mb-4 flex h-4 w-full items-center justify-center text-gray-400">
                            <Icon name="IconScissors" className="mr-2 size-4 shrink-0" />
                            <div className="h-4 min-w-0 flex-1 overflow-hidden pr-2 text-xs">{'---'.repeat(125)}</div>
                          </div>
                        )}
                        <div
                          className="mb-4 flex w-full cursor-pointer items-center justify-between rounded-lg px-4 py-1 hover:bg-slate-50"
                          data-segment-id={paragraph.segmentId}
                          onClick={() => {
                            const playerTime = getPlayerTimeFromSegmentTime(0, clipData, paragraph.segmentId);
                            seekTo(playerTime, false);
                          }}
                        >
                          <div className="flex items-center gap-1.5">
                            <div
                              className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600"
                              title={videoSegmentLabel}
                            >
                              <ClapperIcon className="size-3" />
                              <span className="max-w-[200px] truncate">
                                {truncateDisplayText(getSegmentDisplayTextWithIntroOutroPrefix(videoSegment))}
                              </span>
                            </div>
                            <span className="text-xs text-slate-400">
                              {roundToNDecimalPlaces(paragraph.videoSegmentDetails.duration, 1)}s
                            </span>
                          </div>
                          <span className="text-xs text-slate-400">
                            {getRenderTime ? getRenderTime(0, paragraph.segmentId) : formatTime(videoSegmentStartTime)}
                          </span>
                        </div>
                      </div>
                    );
                  }

                  if (paragraph.chapterDetails) {
                    const chapterStartTime = paragraph?.chapterDetails?.start;
                    const chapterEndTime = paragraph?.chapterDetails?.end || null;

                    const chapterStartRenderTime = isSplitsEnabled
                      ? formatTime(chapterStartTime)
                      : getRenderTime?.(chapterStartTime, paragraph.segmentId) || formatTime(chapterStartTime);

                    return (
                      <div className="py-2 pr-4">
                        {!isFirstParagraph && prevParagraph.segmentId !== paragraph.segmentId && (
                          <div className="mb-4 flex h-4 w-full items-center justify-center text-gray-400">
                            <Icon name="IconScissors" className="mr-2 size-4 shrink-0" />
                            <div className="h-4 min-w-0 flex-1 overflow-hidden pr-2 text-xs">{'---'.repeat(125)}</div>
                          </div>
                        )}
                        <div
                          className="group flex h-9 cursor-pointer select-none items-center justify-between rounded-lg py-1 pl-4 hover:bg-slate-100"
                          onClick={() => {
                            const playerTime = isSplitsEnabled
                              ? chapterStartTime
                              : getPlayerTimeFromSegmentTime(chapterStartTime, clipData, paragraph.segmentId);
                            seekTo(playerTime, false);
                          }}
                        >
                          <div className="space-between flex w-full items-center space-x-3 truncate">
                            <div className="grow truncate text-lg font-semibold" data-testid="transcript-chapter-item">
                              {paragraph?.chapterDetails?.id === selectedChapter?.id ? (
                                <input
                                  className="w-full truncate rounded border-none bg-transparent px-1 font-medium text-black selection:bg-blue-300 selection:text-black focus:border-none focus:outline-none focus:ring-0"
                                  value={selectedChapter?.title}
                                  data-testid="transcript-chapter-rename-input"
                                  onChange={e => setSelectedChapter({ ...selectedChapter, title: e.target.value })}
                                  onBlur={handleRenameSubmit}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      handleRenameSubmit();
                                    }
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <p className="w-full select-none truncate rounded border-none bg-transparent px-1 font-title text-black focus:border-none focus:outline-none focus:ring-0">
                                  {paragraph?.chapterDetails?.title}
                                </p>
                              )}
                            </div>
                            <div className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-400 group-hover:hidden">
                              {chapterStartRenderTime}
                            </div>
                          </div>
                          <span className="hidden group-hover:block">
                            <ChapterActionsMenu
                              type={ChapterType.TRANSCRIPT}
                              chapterId={paragraph.chapterDetails.id}
                              onCreateClip={onCreateClip}
                              chapterStartTime={chapterStartTime}
                              chapterEndTime={chapterEndTime}
                              onRenameChapter={() => handleRenameChapter(paragraph.chapterDetails)}
                            />
                          </span>
                        </div>
                      </div>
                    );
                  }

                  if (paragraph.words) {
                    const previousParagraphRenderTime =
                      getRenderTime && prevParagraph?.speakerDetails?.start
                        ? getRenderTime(
                            timeSpeakerDetailsStringToSeconds(prevParagraph.speakerDetails.start),
                            prevParagraph.segmentId
                          )
                        : prevParagraph?.speakerDetails?.start;
                    const previousParagraphEndRenderTime =
                      !hideEndTimeStamp && getRenderTime && prevParagraph?.speakerDetails?.end
                        ? getRenderTime(
                            timeSpeakerDetailsStringToSeconds(prevParagraph.speakerDetails.end),
                            prevParagraph.segmentId
                          )
                        : prevParagraph?.speakerDetails?.end;
                    const currentSegmentIndex = allSegments.findIndex(segment => segment.id === paragraph.segmentId);
                    const inlinePauses = paragraph.inlinePausesByWordIndex ?? new Map();
                    const deadSpaceEntries = paragraph.deadSpacePauseEntries ?? [];
                    const isPauseEntryInvalid = (entry: TranscriptPauseEntry): boolean => {
                      return invalidPauseEntryKeys.has(getPauseEntryKey(entry));
                    };
                    const visibleDeadSpaceEntries = deadSpaceEntries.filter(entry => !isPauseEntryInvalid(entry));

                    const hasDeadSpaces = isPausesEnabled && visibleDeadSpaceEntries.length > 0;

                    return (
                      <div data-testid="transcript-speaker-container" className="flex w-full">
                        <div
                          data-testid="transcript-paragraph-container"
                          className={classnames('w-full px-4', {
                            'pl-4': prevParagraph?.speakerDetails,
                            'pb-1': !hasDeadSpaces && nextParagraph?.words,
                            'pb-3':
                              !hasDeadSpaces &&
                              (nextParagraph?.speakerDetails ||
                                !(nextParagraph?.words || nextParagraph?.speakerDetails))
                          })}
                        >
                          {prevParagraph?.speakerDetails ? (
                            <div className="mb-2 flex w-full select-none items-center justify-between leading-tight">
                              <div className="-ml-0.5 flex items-center gap-2">
                                <button
                                  data-testid="transcript-speaker-name-button"
                                  className="group flex items-center space-x-1 text-xs font-medium text-slate-700"
                                  onClick={() => handleEditSpeaker(prevParagraph?.speakerDetails?.speaker?.id)}
                                >
                                  <span
                                    className={`${prevParagraph?.speakerDetails?.speaker?.bgColor} ${prevParagraph?.speakerDetails?.speaker?.color} speaker-pill bg-opacity-10 px-2 py-1 font-button`}
                                  >{`${prevParagraph?.speakerDetails?.speaker?.first_name} ${prevParagraph?.speakerDetails?.speaker.last_name}`}</span>
                                  <Icon name="IconEdit" className="hidden size-4 group-hover:flex" />
                                </button>
                              </div>
                              <div
                                data-testid="transcript-speaker-timestamp"
                                className="mr-2 text-xs font-light text-slate-500"
                              >
                                {`${previousParagraphRenderTime}${
                                  hideEndTimeStamp ? '' : `- ${previousParagraphEndRenderTime}`
                                }`}
                              </div>
                            </div>
                          ) : null}
                          {paragraph.hasSomeDeletedBefore && (
                            <div
                              className="inline-flex h-6 w-fit cursor-pointer items-center justify-center rounded-xl bg-slate-100 px-2 py-1 text-slate-400 hover:bg-slate-200"
                              onClick={() => {
                                const segmentAllWords = transcript[paragraph.segmentId];
                                const firstWordStartIndex = segmentAllWords.findIndex(
                                  w => w.start_time === paragraph.words[0].start_time
                                );
                                let newTranscriptStartTime =
                                  firstWordStartIndex !== -1 &&
                                  segmentAllWords[Math.max(firstWordStartIndex - 5, 0)].start_time;

                                const hasAlreadyExtended =
                                  'startSegmentId' in transcriptExtension &&
                                  transcriptExtension.startSegmentId === paragraph.segmentId &&
                                  'startTime' in transcriptExtension &&
                                  transcriptExtension.startTime < paragraph.words[0].start_time;

                                if (hasAlreadyExtended) {
                                  const extendedWordIndex = segmentAllWords.findIndex(
                                    w => w.start_time === transcriptExtension.startTime
                                  );
                                  newTranscriptStartTime =
                                    extendedWordIndex !== -1 &&
                                    segmentAllWords[Math.max(extendedWordIndex - 5, 0)].start_time;
                                }

                                if (typeof newTranscriptStartTime === 'number') {
                                  setTranscriptExtension({
                                    startSegmentId: paragraph.segmentId,
                                    startTime: newTranscriptStartTime
                                  });
                                }
                              }}
                            >
                              <Icon name="IconDots" className="size-4" />
                            </div>
                          )}
                          {isPausesEnabled && inlinePauses.get(-1) && !isPauseEntryInvalid(inlinePauses.get(-1)!) ? (
                            <TranscriptPauseBubble
                              entry={inlinePauses.get(-1)!}
                              onOptimize={onPauseOptimize}
                              onRecover={onPauseRecover}
                              onOptimizeAll={unoptimizedPauses.length > 0 ? handleOptimizeAll : undefined}
                              onRestoreAll={optimizedPauses.length > 0 ? handleRestoreAll : undefined}
                              hasDeleteAtPause={hasDeleteAtPause(
                                inlinePauses.get(-1)!,
                                deletesWithDragChanges[paragraph.segmentId]
                              )}
                              isHighlighted={
                                activeHighlightedPause?.segmentId === paragraph.segmentId &&
                                activeHighlightedPause?.startTime === inlinePauses.get(-1)!.startTime
                              }
                            />
                          ) : null}
                          {paragraph.words?.map((word, i) => {
                            if (
                              isEditing &&
                              transcriptSelection &&
                              editingSegmentId === paragraph.segmentId &&
                              !!transcriptSelection?.words?.[paragraph.segmentId]?.find(
                                w => w.start_time === word.start_time
                              )
                            ) {
                              if (editingStartTime === word.start_time && editingContent) {
                                return (
                                  <TranscriptInlineEdit
                                    key={word.content + i}
                                    editingTranscriptRef={editingTranscriptRef}
                                    paragraphs={paragraphs}
                                    transcriptSelection={transcriptSelection}
                                    editingContent={editingContent}
                                    onCancelEditing={cancelEditing}
                                    transcriptContainer={container}
                                    onEditActionTriggered={onPause}
                                  />
                                );
                              }
                              return <span key={word.content + i}></span>;
                            }

                            const isWordPartOfFirstSelectedSegment =
                              currentSegmentIndex === nearestStartSelectedSegmentIndex &&
                              word.index >= nearestSelectionStartIndex;
                            const isWordPartOfLastSelectedSegment =
                              currentSegmentIndex === nearestEndSelectedSegmentIndex &&
                              word.index <= nearestSelectionEndIndex;
                            const isWordPartOfInternalSelectedSegment =
                              currentSegmentIndex > nearestStartSelectedSegmentIndex &&
                              currentSegmentIndex < nearestEndSelectedSegmentIndex;

                            const shouldHighlightWord =
                              nearestStartSelectedSegmentIndex === nearestEndSelectedSegmentIndex &&
                              currentSegmentIndex === nearestStartSelectedSegmentIndex
                                ? word.index >= nearestSelectionStartIndex && word.index <= nearestSelectionEndIndex
                                : isWordPartOfFirstSelectedSegment ||
                                  isWordPartOfLastSelectedSegment ||
                                  isWordPartOfInternalSelectedSegment;

                            const isCurrentPlaybackWord =
                              !activeHighlightedPause &&
                              activeHighlightedWord?.activeWords.some(w => w.index === word.index) &&
                              activeHighlightedWord?.segmentId === paragraph.segmentId;

                            const deletes = deletesWithDragChanges[paragraph.segmentId] || {};

                            const isDeleted = isWordDeleted(word, deletes);

                            return (
                              <React.Fragment key={word.content + i}>
                                <TranscriptWord
                                  word={word}
                                  segmentId={paragraph.segmentId}
                                  foundWords={highlightedParagraph?.index === index ? highlightedParagraph?.words : []}
                                  forceHighlight={shouldHighlightWord}
                                  isCurrentPlaybackWord={isCurrentPlaybackWord}
                                  isEdit={isEdit}
                                  isWordDeleted={isDeleted}
                                  isFullClip={isFullClip}
                                  isClipFromRS={isClipFromRS}
                                />
                                {isPausesEnabled &&
                                inlinePauses.get(i) &&
                                !isPauseEntryInvalid(inlinePauses.get(i)!) ? (
                                  <TranscriptPauseBubble
                                    entry={inlinePauses.get(i)!}
                                    onOptimize={onPauseOptimize}
                                    onRecover={onPauseRecover}
                                    onOptimizeAll={unoptimizedPauses.length > 0 ? handleOptimizeAll : undefined}
                                    onRestoreAll={optimizedPauses.length > 0 ? handleRestoreAll : undefined}
                                    hasDeleteAtPause={hasDeleteAtPause(
                                      inlinePauses.get(i)!,
                                      deletesWithDragChanges[paragraph.segmentId]
                                    )}
                                    isHighlighted={
                                      activeHighlightedPause?.segmentId === paragraph.segmentId &&
                                      activeHighlightedPause?.startTime === inlinePauses.get(i)!.startTime
                                    }
                                  />
                                ) : null}
                              </React.Fragment>
                            );
                          })}
                          {paragraph.hasSomeDeletedAfter && (
                            <div
                              className="inline-flex h-6 w-fit cursor-pointer items-center justify-center rounded-xl bg-slate-100 px-2 py-1 text-slate-400 hover:bg-slate-200"
                              onClick={() => {
                                const segmentAllWords = transcript[paragraph.segmentId];
                                const lastWordEndIndex = segmentAllWords.findIndex(
                                  w => w.end_time === paragraph.words[paragraph.words.length - 1].end_time
                                );
                                let newTranscriptEndTime =
                                  lastWordEndIndex !== -1 &&
                                  segmentAllWords[Math.min(lastWordEndIndex + 5, segmentAllWords.length - 1)].end_time;

                                const hasAlreadyExtended =
                                  'endSegmentId' in transcriptExtension &&
                                  transcriptExtension.endSegmentId === paragraph.segmentId &&
                                  'endTime' in transcriptExtension &&
                                  transcriptExtension.endTime > paragraph.words[paragraph.words.length - 1].end_time;

                                if (hasAlreadyExtended) {
                                  const extendedWordIndex = segmentAllWords.findIndex(
                                    w => w.end_time === transcriptExtension.endTime
                                  );
                                  newTranscriptEndTime =
                                    extendedWordIndex !== -1 &&
                                    segmentAllWords[Math.min(extendedWordIndex + 5, segmentAllWords.length - 1)]
                                      .end_time;
                                }

                                if (typeof newTranscriptEndTime === 'number') {
                                  setTranscriptExtension({
                                    endSegmentId: paragraph.segmentId,
                                    endTime: newTranscriptEndTime
                                  });
                                }
                              }}
                            >
                              <Icon name="IconDots" className="size-4" />
                            </div>
                          )}

                          {isPausesEnabled &&
                            visibleDeadSpaceEntries.length > 0 &&
                            (() => {
                              return visibleDeadSpaceEntries.length > 0 ? (
                                <TranscriptDeadSpaceRow
                                  entries={visibleDeadSpaceEntries}
                                  onOptimize={onPauseOptimize}
                                  onRecover={onPauseRecover}
                                  onOptimizeAll={unoptimizedPauses.length > 0 ? handleOptimizeAll : undefined}
                                  onRestoreAll={optimizedPauses.length > 0 ? handleRestoreAll : undefined}
                                  hasDeleteFlags={visibleDeadSpaceEntries.map(entry =>
                                    hasDeleteAtPause(entry, deletesWithDragChanges[paragraph.segmentId])
                                  )}
                                  isHighlighted={visibleDeadSpaceEntries.some(
                                    entry =>
                                      activeHighlightedPause?.segmentId === paragraph.segmentId &&
                                      activeHighlightedPause?.startTime === entry.startTime
                                  )}
                                />
                              ) : null;
                            })()}

                          {isChaptersEnabled && !nextParagraph?.chapterDetails && paragraphs.length !== index + 1 && (
                            <div
                              className={classnames('group flex h-4 w-full items-center justify-center', {
                                'transition-all delay-700 duration-700 ease-in-out hover:h-10':
                                  nearestSelectionStartIndex === -1 && nearestSelectionEndIndex === -1
                              })}
                            >
                              {nearestSelectionStartIndex === -1 && nearestSelectionEndIndex === -1 && (
                                <button
                                  ref={el => {
                                    chapterButtonRefs.current[index] = el;
                                  }}
                                  className="flex w-full items-center justify-center rounded-lg bg-slate-100 py-1.5 text-center text-xs font-medium opacity-0 transition-all delay-700 duration-700 ease-in-out group-hover:opacity-100"
                                  onClick={() => onCreateNewChapter(paragraph.words, index, paragraph.segmentId)}
                                >
                                  <Icon name="IconList" className="mr-1 size-4" />
                                  <span>New Chapter</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return null;
                }}
              />
            </div>
          )}
        </div>
      </div>
      {isEdit &&
      selectionStartTime !== null &&
      selectionEndTime !== null &&
      startSegmentId !== null &&
      endSegmentId !== null ? (
        <EditTranscriptMenu
          editPopupRef={editPopupRef}
          isCreatingClip={isCreatingClip}
          transcriptSelection={transcriptSelection}
          show={showEditMenu && !isSelecting}
          transcriptExtension={transcriptExtension}
          onCreate={createFullRecordingClip}
          onClose={cleanUpSelection}
          onStartEditing={startEditing}
          onEditActionTriggered={onPause}
          selectionStartTime={selectionStartTime}
          selectionEndTime={selectionEndTime}
          selectionStartSegmentId={startSegmentId}
          selectionEndSegmentId={endSegmentId}
        />
      ) : (
        <CreateClipPopup
          createClipPopup={createClipPopup}
          createClipWrapper={createClipWrapper}
          transcriptSelection={transcriptSelection}
          onCreate={onClickCreateClipButton}
          isCreatingClip={isCreatingClip}
        />
      )}
      <CaptionLanguageSelectionDialog
        clipData={clipData}
        isOpen={isCaptionTranslationModalOpen}
        onClose={closeCaptionTranslationModal}
        onConfirm={onCaptionTranslation}
      />
    </React.Fragment>
  );
}

export default memo(FullTranscript);
